import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Disclosure } from "@/components/Disclosure";
import { cerrarOF } from "@/ts/api/API";
import { Breadcrumb } from "@/components/Breadcrumb";
import { notificacion, notificacionTipo } from "@/components/Alerts";
import { TablaFasesOF } from "./TablaFasesOF";
import { TablaOrdenesGestor } from "./TablaOrdenesGestor";
import { Loading, QueryError } from "@/components/Loading/Loading";
import { usePuestosConCarga } from "@/hooks/Api/Puestos";
import type { InfoOF } from "@/hooks/Api/OrdenesFabricacion";
import "./GestionOF.css";
import type { RowSelectionState } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { ModalConfirmar } from "@/components/modals/Confirmar";

export function GestionOFsPage() {
	const queryClient = useQueryClient();
	const [filtroMaquina, setFiltroMaquina] = useState<string | undefined>(undefined);
	const [ofFocused, setOfFocused] = useState<InfoOF | undefined>(undefined);
	const [selectedOFs, setSelectedOFs] = useState<RowSelectionState>({});
	const [ofsACerrar, setOfsACerrar] = useState<number[]>([]);


	const onCerrarOFsSeleccionadas = () => {
		const ofsACerrar = Object.entries(selectedOFs).map(([id, _]) => Number(id));

		if (ofsACerrar.length === 0) {
			notificacionTipo("warning", "No hay OFs seleccionadas.");
			return;
		}

		setOfsACerrar(ofsACerrar);
	};

	return (
		<div id="content" className="content" >
			<title>Piher | Gestión de OFs </title>
			<Breadcrumb
				sectionTitle="Gestión de OFs"
				links={[
					{ label: "Gestión de OFs", active: true }
				]}
			/>

			<Disclosure style={{ marginBottom: 20 }}>
				<h4 className="panel-title panel-heading senalar">
					Filtro
				</h4>
				<Filtros filtroMaquina={filtroMaquina} setFiltroMaquina={setFiltroMaquina} />
			</Disclosure>

			<TablaOrdenesGestor
				filtroMaquina={filtroMaquina}
				ofFocused={ofFocused}
				setOfFocused={setOfFocused}
				selectedOFs={selectedOFs}
				setSelectedOFs={setSelectedOFs}
			/>

			<div className="contenedorBotones">
				<button className="btnPrimary" onClick={onCerrarOFsSeleccionadas} type="button">
					Archivar OFs seleccionadas
				</button>
			</div>

			{ofFocused ? (
				<div className="table-responsive" style={{ marginTop: "2em" }}>
					<TablaFasesOF of={ofFocused} />
				</div>
			) : null}

			<ModalConfirmar
				show={ofsACerrar.length > 0}
				close={() => setOfsACerrar([])}
				message={`¿Está seguro de que desea archivar ${ofsACerrar.length} OFs de forma permanente?`}
				onConfirm={() => {
					const errores = new Array<string>();
					const promesas = new Array<Promise<unknown>>();

					for (const idOF of ofsACerrar) {
						const promesa = cerrarOF(idOF).catch((error: unknown) => {
							errores.push(`Error cerrando OF ${idOF}.`);
							console.error(`Error cerrando OF ${idOF}`, error);
						});

						promesas.push(promesa);
					}

					void Promise.all(promesas).then(() => {
						if (errores.length > 0) {
							toast(errores.join("\n"), { type: "error" });
						} else {
							notificacion("OFs cerradas correctamente.");
							setOfsACerrar([]);
						}

						return queryClient.invalidateQueries({ queryKey: ["getOFsEnCurso"] });
					});
				}}
			/>
		</div>
	);
}

interface FiltrosProps {
	readonly filtroMaquina?: string;
	readonly setFiltroMaquina: (maquina?: string) => void;
}
function Filtros(props: FiltrosProps) {
	const onChangeMaquina = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.target.value;
		if (value === "") {
			props.setFiltroMaquina(undefined);
		} else {
			props.setFiltroMaquina(value);
		}
	};

	const { data, error, isPending } = usePuestosConCarga();

	if (error) return <QueryError error={error} fallback="Error al cargar las maquinas" />;
	if (isPending) return <Loading />;

	return (
		<div style={{ display: "flex", alignItems: "flex-end", gap: 15, padding: 30 }} role="tabpanel">
			<label style={{ fontSize: "13pt" }}>Puestos: </label>
			<select className="form-control" onChange={onChangeMaquina} value={props.filtroMaquina ?? ""}>
				<option value="">Elige</option>
				{data.map((maquina) => (
					<option value={maquina.idPuesto} key={maquina.idPuesto}>
						{maquina.descripcion}
						-
						Carga de trabajo: {maquina.cantidadHoras}h
					</option>
				))}
			</select>
		</div>
	);
}
